import { useEffect, useState } from "react";
import {
  Antenna, Ban, Building2, Calendar, CalendarClock, Car, Check, ChevronDown, ChevronUp, Clock, Loader2, MapPin, Phone, Route, Send, Timer, User, X, Zap,
} from "lucide-react";
import api from "../../utils/api";
import { prazoDoAcionamento } from "../../utils/portalPrazo";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Avaliacao } from "../../pages/portals/PortalDetectionsTab";
import ServiceOSLink from "../../pages/portals/ServiceOSLink";
import ServiceTypeSelect, { tiposComoOpcoes } from "../ServiceTypeSelect";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const decorrido = (iso) => {
  const ms = Date.now() - new Date(iso || Date.now()).getTime();
  const s = Math.max(Math.floor(ms / 1000), 0);
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `há ${h}h ${m % 60}m`;
};

/** Cronômetro do acionamento: o tempo de resposta é o que decide a corrida. */
const Cronometro = ({ desde }) => {
  const [texto, setTexto] = useState(() => decorrido(desde));
  useEffect(() => {
    const t = setInterval(() => setTexto(decorrido(desde)), 1000);
    return () => clearInterval(t);
  }, [desde]);
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 tabular-nums shrink-0">
      <Clock size={11} /> {texto}
    </span>
  );
};

/** Prazo do portal para responder (`acceptance_ttl`): zerou, o aviso sai da tela. */
const Prazo = ({ ate, onExpirar }) => {
  const restante = () => Math.floor((new Date(ate).getTime() - Date.now()) / 1000);
  const [segundos, setSegundos] = useState(restante);
  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.floor((new Date(ate).getTime() - Date.now()) / 1000);
      setSegundos(s);
      if (s <= 0) onExpirar();
    }, 1000);
    return () => clearInterval(t);
  }, [ate, onExpirar]);
  const s = Math.max(segundos, 0);
  const critico = s <= 30;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded shrink-0 ${
        critico ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
      }`}
      title="Prazo do portal para aceitar"
      data-testid="portal-accept-countdown"
    >
      <Timer size={11} />
      {String(Math.floor(s / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}
    </span>
  );
};

const Linha = ({ icon: Icon, children, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <Icon size={13} className="text-gray-400 shrink-0 mt-[2px]" />
    <p className="text-[12px] text-gray-700 leading-snug min-w-0 break-words">{children}</p>
  </div>
);

const Endereco = ({ rotulo, dados }) => {
  const rua = [dados?.endereco, dados?.numero].filter(Boolean).join(", ");
  const linha = [rua, dados?.bairro, dados?.cidade && `${dados.cidade} - ${dados.uf || ""}`.trim()]
    .filter(Boolean)
    .join(", ");
  // Complemento e referência ajudam o operador a decidir e a orientar o motorista.
  const extra = [dados?.complemento, dados?.referencia].filter(Boolean).join(" · ");
  if (!linha && !dados?.cep) return null;
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{rotulo}</p>
      <p className="text-[12px] text-gray-800 leading-snug break-words">{linha || "—"}</p>
      {extra && <p className="text-[10px] text-gray-500 leading-snug break-words">{extra}</p>}
      {dados?.cep && <p className="text-[10px] text-gray-400">CEP {dados.cep}</p>}
    </div>
  );
};

/**
 * Acionamento em formato de aviso no canto da tela: o operador continua
 * trabalhando e decide sem perder o contexto da página. Tudo que pesa na
 * decisão fica visível — endereços, veículo, prazo e por que a regra não casou.
 */
export default function PortalDetectionCard({
  det, motivos, salvando, onAceitar, onRecusar, onDispensar, onExpirar, onPedirMotivos,
  onDefinirTipo,
}) {
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [motorista, setMotorista] = useState(undefined);
  const [aberto, setAberto] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [tipoEscolhido, setTipoEscolhido] = useState("");

  // Acionamento aceito sem de-para: o tipo de serviço é escolhido aqui e a
  // escolha vira sugestão de de-para para o super admin confirmar.
  const precisaTipo = !!det.needs_service_type && !det.service_id;
  // Aceito (aqui, por um colega ou pelo webhook da automação): o cartão deixa de
  // ser uma decisão e passa a ser confirmação — sem botões, saindo sozinho.
  const aceito = det.status === "accepted" && !precisaTipo;
  useEffect(() => {
    if (!precisaTipo || tipos.length) return;
    api
      .get("/service-types")
      .then(({ data }) => setTipos((data || []).filter((t) => t.active !== false)))
      .catch(() => setTipos([]));
  }, [precisaTipo, tipos.length]);

  // Motorista mais próximo: mesma base do painel logístico (posição do app +
  // provider de rota do tenant). Sem isso o operador aceita sem saber se chega.
  // Já aceito não tem decisão a tomar: nada a calcular.
  useEffect(() => {
    if (aceito) return undefined;
    let vivo = true;
    api
      .post("/portals/detections/nearest", {
        endereco: [det.origin?.endereco, det.origin?.numero].filter(Boolean).join(", "),
        bairro: det.origin?.bairro || "",
        cidade: det.origin?.cidade || "",
        uf: det.origin?.uf || "",
        cep: det.origin?.cep || "",
      })
      .then(({ data }) => vivo && setMotorista(data))
      .catch(() => vivo && setMotorista(null));
    return () => {
      vivo = false;
    };
  }, [aceito, det.id, det.origin]);

  const teste = !!det.teste;
  const agendado = det.attendance_type === "SCHEDULED";
  const recusado = det.status === "rejected" && !!det.decided_by;
  const indisponivel = !!det.indisponivel_aceite && !aceito;
  const aguardandoPortal = !!det.awaiting_webhook && !aceito && !recusado && !det.service_id && !indisponivel;
  const podeDecidir = !teste && !aceito && !recusado && !aguardandoPortal && !indisponivel;
  const aceitoPor = det.decided_by?.name || "";
  const pedidoPor = det.accept_requested_by?.name || "";
  const destino = det.destination?.cidade || det.destination?.endereco;
  const veiculo = [det.vehicle?.marca, det.vehicle?.modelo, det.vehicle?.placa, det.vehicle?.cor]
    .filter(Boolean)
    .join(" · ");
  const telefones = det.customer_phone || det.contact_phone;
  const prazo = det.scheduled_for
    ? new Date(det.scheduled_for).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "";
  // Agendado mostra a janela marcada; emergencial mostra a previsão do portal
  // (`max_sla`, em minutos, contado da detecção).
  const quando = prazoDoAcionamento(det);

  return (
    <div
      // `shrink-0`: a pilha é uma coluna flex com altura máxima — sem isto o
      // cartão era comprimido e o `overflow-hidden` cortava os botões.
      // `saindo`: fechamento automático do aviso de aceite — sai deslizando
      // para a direita, em vez de desaparecer de uma vez.
      className={`w-[380px] shrink-0 rounded-xl border bg-white shadow-[0_12px_32px_-12px_rgba(1,42,54,0.35)] overflow-hidden transition-colors duration-300 ${
        aceito ? "border-emerald-300" : "border-gray-200"
      } ${
        det.saindo
          ? "animate-out slide-out-to-right-8 fade-out duration-300 fill-mode-forwards"
          : "animate-in slide-in-from-right-4 fade-in duration-300"
      }`}
      data-testid={`portal-detection-toast-${det.id}`}
      data-aceito={aceito ? "true" : "false"}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 border-b transition-colors duration-300 ${
          aceito ? "border-emerald-100 bg-emerald-50/80" : "border-gray-100 bg-gray-50/80"
        }`}
      >
        <Antenna
          size={13}
          className={`shrink-0 ${aceito ? "text-emerald-700" : "text-emerald-600"}`}
        />
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 flex-1 truncate" data-testid={`portal-detection-source-${det.id}`}>
          {det.portal_name
            ? `${teste ? "Teste · " : ""}${det.portal_name}`
            : teste ? "Acionamento de teste" : "Acionamento detectado"}
        </p>
        <Cronometro desde={det.detected_at} />
        {/* Aceito não tem mais prazo para responder: o relógio sairia contando
            um prazo que já não existe e ao zerar encerraria o aviso por "expirado".
            Vale também para o aceite que só espera o tipo de serviço — ele já
            está garantido no portal e o cartão não pode sumir da tela. */}
        {det.accept_until && !aceito && !precisaTipo && (
          <Prazo ate={det.accept_until} onExpirar={onExpirar} />
        )}
        <button
          type="button"
          onClick={onDispensar}
          className="text-gray-400 hover:text-gray-700 transition-colors shrink-0"
          title="Fechar só na minha tela"
          data-testid={`portal-toast-close-${det.id}`}
        >
          <X size={14} />
        </button>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => setAberto((v) => !v)}
        onKeyDown={(e) => e.key === "Enter" && setAberto((v) => !v)}
        className="px-3 py-2.5 space-y-2.5 max-h-[58vh] overflow-y-auto cursor-pointer"
        data-testid={`portal-toast-body-${det.id}`}
      >
        {/* Serviço aceito: a confirmação vem primeiro, antes dos dados — é a
            única informação que o operador precisa ler neste estado. */}
        {aceito && (
          <div
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 animate-in fade-in zoom-in-95 duration-300"
            data-testid={`portal-accepted-banner-${det.id}`}
          >
            <span className="h-6 w-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <Check size={14} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[12px] font-bold text-emerald-900">
                Serviço aceito
              </span>
              <span className="block text-[10px] text-emerald-800 truncate">
                {aceitoPor ? `por ${aceitoPor}` : "aceite confirmado no portal"}
              </span>
            </span>
          </div>
        )}

        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-gray-900 tabular-nums leading-none">
              {det.protocol || det.external_service_id || "—"}
            </p>
            <p className="text-[11px] font-semibold uppercase text-gray-500 mt-1 break-words">
              {det.service_type || "Serviço não informado"}
            </p>
          </div>
          <span
            className="text-gray-400 shrink-0 mt-0.5"
            title={aberto ? "Recolher" : "Ver tudo"}
            data-testid={`portal-toast-toggle-${det.id}`}
          >
            {aberto ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </span>
        </div>

        <div className="flex items-center flex-wrap gap-1.5">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
              agendado ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"
            }`}
          >
            <Zap size={9} /> {agendado ? "Agendado" : "Emergencial"}
          </span>
          {/* Quando o serviço tem que acontecer: sem isto o operador tinha que
              abrir o cartão para ver a data do agendamento. */}
          {quando && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
              data-testid={`portal-detection-prazo-${det.id}`}
            >
              <CalendarClock size={9} />
              {quando}
            </span>
          )}
          <Avaliacao det={det} />
          {det.matched_rule_set_name && (
            <span className="text-[10px] text-gray-400 truncate">{det.matched_rule_set_name}</span>
          )}
        </div>

        {aberto ? (
          <Endereco rotulo="Origem" dados={det.origin} />
        ) : (
          <Linha icon={MapPin}>
            {[det.origin?.bairro, det.origin?.cidade, det.origin?.uf].filter(Boolean).join(", ") ||
              "Origem não informada"}
          </Linha>
        )}
        {aberto && destino && <Endereco rotulo="Destino" dados={det.destination} />}

        {/* Quem está mais perto — decide aceitar ou recusar. Some quando já
            está aceito: não há decisão, e o bloco só ocuparia espaço. */}
        {!aceito && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50/80 px-2.5 py-2"
          data-testid={`portal-nearest-${det.id}`}
        >
          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
            Motorista mais próximo
          </p>
          {motorista === undefined ? (
            <p className="text-[11px] text-gray-400 mt-0.5 inline-flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> calculando…
            </p>
          ) : motorista?.professional ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="h-6 w-6 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                {(motorista.professional.nome || "?")[0]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-semibold text-gray-900 truncate">
                  {motorista.professional.nome || "Sem nome"}
                  {motorista.professional.placa ? ` · ${motorista.professional.placa}` : ""}
                </span>
                <span className="block text-[10px] text-gray-500">
                  {motorista.professional.distancia_km} km
                  {motorista.professional.eta_min != null
                    ? ` · chega em ~${motorista.professional.eta_min} min`
                    : " · tempo indisponível"}
                </span>
                {/* Posição velha ou app fora do ar: o operador precisa saber
                    que a distância é da última posição conhecida. */}
                {motorista.professional.presenca !== "online" && (
                  <span
                    className="block text-[10px] text-amber-700"
                    data-testid={`portal-nearest-stale-${det.id}`}
                  >
                    {motorista.professional.presenca === "instavel"
                      ? "conexão instável"
                      : "app fora do ar"}
                    {motorista.professional.posicao_idade_min != null
                      ? ` · posição de ${motorista.professional.posicao_idade_min} min atrás`
                      : ""}
                  </span>
                )}
              </span>
              {motorista.candidatos > 1 && (
                <span className="text-[10px] text-gray-400 shrink-0">
                  +{motorista.candidatos - 1}
                </span>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 mt-0.5">
              {motorista?.motivo || "Nenhum motorista disponível agora."}
            </p>
          )}
        </div>
        )}

        {aberto && det.contractor_name && (
          <Linha icon={Building2}>
            {det.contractor_name}
            {det.product_name ? ` · ${det.product_name}` : ""}
            {det.plan_name ? ` · ${det.plan_name}` : ""}
          </Linha>
        )}
        {aberto && (det.customer_name || det.contact_name) && (
          <Linha icon={User}>{det.customer_name || det.contact_name}</Linha>
        )}
        {aberto && telefones && (
          <Linha icon={Phone}>
            <a href={`tel:${telefones}`} className="hover:underline">
              {telefones}
            </a>
          </Linha>
        )}
        {aberto && veiculo && <Linha icon={Car}>{veiculo}</Linha>}
        {aberto && prazo && (
          <Linha icon={Calendar}>
            Agendado para <strong className="font-semibold">{prazo}</strong>
          </Linha>
        )}
        {aberto && (det.origin?.zona || det.destination?.zona) && (
          <Linha icon={Route}>
            {[det.origin?.zona && `origem ${det.origin.zona}`, det.destination?.zona && `destino ${det.destination.zona}`]
              .filter(Boolean)
              .join(" · ")}
          </Linha>
        )}
        {aberto && det.problem_description && (
          <p className="text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 leading-snug">
            {det.problem_description}
          </p>
        )}
        {!aberto && (
          <p className="text-[10px] text-gray-400">
            Clique para ver cliente, telefone, veículo e descrição
          </p>
        )}
        {teste && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
            Simulação: nada foi gravado e não há OS para criar.
          </p>
        )}

        {recusando && (
          <div className="space-y-2 pt-0.5" onClick={(e) => e.stopPropagation()}>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger className="h-8 text-xs" data-testid={`portal-reject-reason-${det.id}`}>
                <SelectValue placeholder="Motivo da recusa (exigido pelo portal)" />
              </SelectTrigger>
              <SelectContent className="z-[1900]">
                {motivos.map((m) => (
                  <SelectItem key={m.code || m.id} value={m.code || m.id}>
                    {m.description || m.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Observação (opcional)"
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              className="text-xs min-h-[56px]"
              data-testid={`portal-reject-notes-${det.id}`}
            />
          </div>
        )}
      </div>

      {det.auto_dispatch && (
        <div
          className={`px-3 py-1.5 border-t text-[11px] flex items-center gap-1.5 ${
            det.auto_dispatch.status === "ok"
              ? "border-emerald-100 bg-emerald-50/60 text-emerald-800"
              : "border-red-100 bg-red-50/70 text-red-700"
          }`}
          data-testid={`portal-auto-dispatch-${det.id}`}
        >
          <Send size={12} className="shrink-0" />
          <span className="leading-snug">
            {det.auto_dispatch.status === "ok"
              ? `Motorista ${det.auto_dispatch.nome || ""} acionado.`
              : `Não foi possível acionar ${det.auto_dispatch.nome || "o motorista"}: ${det.auto_dispatch.motivo || ""}`}
          </span>
        </div>
      )}
      {det.service_id ? (
        <div
          className="px-3 py-2 border-t border-gray-100 bg-emerald-50/60 flex items-center gap-2"
          data-testid={`portal-os-created-${det.id}`}
        >
          <span className="text-[11px] text-emerald-800 font-semibold shrink-0">OS criada:</span>
          <ServiceOSLink
            serviceId={det.service_id}
            protocolo={det.service_protocolo}
            cancelado={det.service_cancelado}
            testId={`portal-os-link-${det.id}`}
            compacto
          />
          <span className="flex-1" />
          <Button
            variant="outline"
            className="h-8 gap-1.5 text-[11px] bg-white"
            onClick={onDispensar}
            data-testid={`portal-os-close-${det.id}`}
          >
            <Check size={13} /> Fechar
          </Button>
        </div>
      ) : precisaTipo ? (
        <div
          className="px-3 py-2.5 border-t border-gray-100 bg-amber-50/60 space-y-2"
          data-testid={`portal-need-type-${det.id}`}
        >
          <p className="text-[11px] text-amber-800 leading-snug">
            Aceito no portal. Este tipo ainda não tem de-para configurado — escolha o tipo de
            serviço do AutEM para abrir a OS.
          </p>
          <ServiceTypeSelect
            options={tiposComoOpcoes(tipos)}
            value={tipoEscolhido}
            onChange={(folha) => setTipoEscolhido(folha?.id || "")}
            placeholder="Tipo de serviço"
            emptyText="Nenhum tipo cadastrado"
            testid={`portal-type-select-${det.id}`}
          />
          <Button
            className="h-8 w-full gap-1.5 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={!tipoEscolhido || salvando}
            onClick={() => onDefinirTipo(tipoEscolhido)}
            data-testid={`portal-type-confirm-${det.id}`}
          >
            {salvando ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Criar OS com este tipo
          </Button>
        </div>
      ) : podeDecidir ? (
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/70 flex items-center gap-2">          {recusando ? (
            <>
              <Button
                variant="ghost"
                className="h-8 flex-1 text-[11px] text-gray-500"
                onClick={() => setRecusando(false)}
                data-testid={`portal-reject-cancel-${det.id}`}
              >
                Voltar
              </Button>
              <Button
                variant="destructive"
                className="h-8 flex-1 gap-1.5 text-[11px]"
                disabled={salvando}
                onClick={() => onRecusar(motivo, obs)}
                data-testid={`portal-reject-confirm-${det.id}`}
              >
                <X size={13} /> Confirmar recusa
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                className="h-8 flex-1 min-w-0 gap-1 px-1.5 text-[11px] font-bold bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                disabled={salvando}
                onClick={() => {
                  setRecusando(true);
                  onPedirMotivos(det);
                }}
                data-testid={`portal-reject-${det.id}`}
              >
                <X size={13} /> RECUSAR
              </Button>
              {motorista?.professional?.person_id ? (
                <div className="flex-[1.6] min-w-0 flex">
                  <Button
                    className="h-8 flex-1 min-w-0 gap-1.5 px-2 rounded-r-none text-[11px] font-bold bg-[var(--accent)] hover:opacity-90 text-white"
                    disabled={salvando}
                    onClick={() => onAceitar(motorista.professional)}
                    title={`Aceita no portal e, ao criar a OS, aciona ${motorista.professional.nome || "o motorista"}`}
                    data-testid={`portal-accept-dispatch-${det.id}`}
                  >
                    <Send size={12} className="shrink-0" />
                    <span className="truncate">ACEITAR E ACIONAR</span>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="h-8 w-8 shrink-0 p-0 rounded-l-none border-l border-white/30 bg-[var(--accent)] hover:opacity-90 text-white"
                        disabled={salvando}
                        aria-label="Outras opções de aceite"
                        data-testid={`portal-accept-menu-${det.id}`}
                      >
                        <ChevronDown size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="z-[1900]">
                      <DropdownMenuItem
                        className="text-xs font-semibold gap-2"
                        onClick={() => onAceitar()}
                        data-testid={`portal-accept-${det.id}`}
                      >
                        <Check size={13} className="text-emerald-600" /> Somente aceitar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <Button
                  className="h-8 flex-1 min-w-0 gap-1 px-1.5 text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={salvando}
                  onClick={() => onAceitar()}
                  data-testid={`portal-accept-${det.id}`}
                >
                  <Check size={13} /> ACEITAR
                </Button>
              )}
            </>
          )}
        </div>
      ) : indisponivel ? (
        <div
          className="px-3 py-2 border-t border-amber-100 bg-amber-50/70 flex items-center gap-2"
          data-testid={`portal-unavailable-footer-${det.id}`}
        >
          <Ban size={13} className="text-amber-700 shrink-0" />
          <span className="text-[11px] text-amber-800 flex-1 leading-snug">
            Este serviço não está mais disponível para aceite no portal. O aviso fecha sozinho.
          </span>
        </div>
      ) : recusado ? (
        <div
          className="px-3 py-2 border-t border-red-100 bg-red-50/70 flex items-center gap-2"
          data-testid={`portal-rejected-footer-${det.id}`}
        >
          <X size={13} className="text-red-700 shrink-0" />
          <span className="text-[11px] text-red-800 flex-1 leading-snug">
            Recusado no portal{aceitoPor ? ` por ${aceitoPor}` : ""}
            {det.rejection_notes ? ` — ${det.rejection_notes}` : det.rejection_code ? ` — ${det.rejection_code}` : ""}.
            O aviso fecha sozinho.
          </span>
        </div>
      ) : aguardandoPortal ? (
        <div
          className="px-3 py-2 border-t border-amber-100 bg-amber-50/60 flex items-center gap-2"
          data-testid={`portal-awaiting-webhook-${det.id}`}
        >
          <Loader2 size={13} className="animate-spin text-amber-700 shrink-0" />
          <span className="text-[11px] text-amber-800 flex-1 leading-snug">
            Aceite enviado ao portal{pedidoPor ? ` por ${pedidoPor}` : ""}. Aguardando o portal
            confirmar para marcar como aceito e criar a OS
            {det.dispatch_person_nome ? ` e acionar ${det.dispatch_person_nome}` : ""}…
          </span>
          <Button
            variant="outline"
            className="h-8 gap-1.5 text-[11px] bg-white"
            onClick={onDispensar}
            data-testid={`portal-awaiting-close-${det.id}`}
          >
            <X size={13} /> Fechar
          </Button>
        </div>
      ) : aceito ? (
        <div
          className="px-3 py-2 border-t border-emerald-100 bg-emerald-50/60 flex items-center gap-2"
          data-testid={`portal-accepted-footer-${det.id}`}
        >
          <span className="text-[11px] text-emerald-800 flex-1 leading-snug">
            Nada a decidir — o aviso fecha sozinho.
          </span>
          <Button
            variant="outline"
            className="h-8 gap-1.5 text-[11px] bg-white"
            onClick={onDispensar}
            data-testid={`portal-accepted-close-${det.id}`}
          >
            <Check size={13} /> Fechar agora
          </Button>
        </div>
      ) : (
        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/70 flex justify-end">
          <Button
            variant="outline"
            className="h-8 gap-1.5 text-[11px] bg-white"
            onClick={onDispensar}
            data-testid={`portal-test-close-${det.id}`}
          >
            <Check size={13} /> Fechar simulação
          </Button>
        </div>
      )}
    </div>
  );
}
